/**
 * Usage Section
 *
 * Displays the current user's usage statistics including:
 * - Total reflections count
 * - This month's reflections
 * - Total audio minutes transcribed
 * - Member since date
 */

import { AlertCircle, Clock, Cpu, FileText, Calendar, Mic } from "lucide-react";
import { Card } from "../ui/card";
import { useUserUsage } from "../../hooks/useUsage";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useSubscriptionStatus } from "../../hooks/useBilling";

/**
 * Format transcription time in human-friendly units.
 * Returns "X.X min" for < 60, "X.X hrs" for < 1440, "X.X days" otherwise.
 */
function formatTranscriptionTime(minutes: number): {
  value: string;
  unit: string;
} {
  if (minutes < 60) {
    return { value: minutes.toFixed(1), unit: "min" };
  } else if (minutes < 1440) {
    return { value: (minutes / 60).toFixed(1), unit: "hrs" };
  } else {
    return { value: (minutes / 1440).toFixed(1), unit: "days" };
  }
}

function CostProgressBar({
  used,
  limit,
  label,
  isAdmin,
}: {
  used: number;
  limit: number;
  label: string;
  isAdmin: boolean;
}) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const barColor =
    pct >= 100
      ? "bg-red-500 dark:bg-red-600"
      : pct >= 80
        ? "bg-amber-500 dark:bg-amber-600"
        : "bg-blue-500 dark:bg-blue-600";

  return (
    <div>
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>{label}</span>
        <span>
          {isAdmin
            ? `$${used.toFixed(2)} / $${limit.toFixed(2)}`
            : `${Math.round(pct)}% used`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function UsageSection() {
  const { data: user } = useCurrentUser();
  const { data: usage, isLoading, error } = useUserUsage();
  const { data: subscriptionStatus } = useSubscriptionStatus();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="animate-pulse">
            <div className="mb-4 h-6 w-48 rounded bg-muted" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 rounded bg-muted" />
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-destructive">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load usage information. Please try again.</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">Your Usage</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Overview of your activity and resource usage on AttuneWell.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span className="text-sm font-medium">Total Reflections</span>
            </div>
            <p className="mt-2 text-3xl font-bold text-foreground">
              {usage?.total_reflections ?? 0}
            </p>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">This Month</span>
            </div>
            <p className="mt-2 text-3xl font-bold text-foreground">
              {usage?.reflections_this_month ?? 0}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">reflections</p>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">Audio Transcribed</span>
            </div>
            {(() => {
              const t = formatTranscriptionTime(
                usage?.total_audio_minutes ?? 0
              );
              return (
                <>
                  <p className="mt-2 text-3xl font-bold text-foreground">
                    {t.value}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{t.unit}</p>
                </>
              );
            })()}
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">Member Since</span>
            </div>
            <p className="mt-2 text-lg font-bold text-foreground">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                : "N/A"}
            </p>
          </div>
        </div>
      </Card>

      {usage && usage.total_reflections > 0 && (
        <Card className="p-6">
          <h3 className="mb-4 text-md font-semibold text-foreground">
            Usage Details
          </h3>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Average reflection length
              </dt>
              <dd className="text-sm text-foreground">
                {usage.average_audio_minutes?.toFixed(1) ?? "N/A"} minutes
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Last reflection
              </dt>
              <dd className="text-sm text-foreground">
                {usage.last_reflection_date
                  ? new Date(usage.last_reflection_date).toLocaleDateString()
                  : "N/A"}
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {/* Resource Usage */}
      {usage &&
        (usage.total_input_tokens + usage.total_output_tokens > 0 ||
          usage.total_audio_minutes > 0) && (
          <Card className="p-6">
            <h3 className="mb-4 text-md font-semibold text-foreground">
              Resource Usage
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Cpu className="h-4 w-4" />
                  <p className="text-sm font-medium">LLM Tokens Used</p>
                </div>
                <p className="mt-2 text-2xl font-bold text-foreground">
                  {(
                    usage.total_input_tokens + usage.total_output_tokens
                  ).toLocaleString()}
                  {user?.is_admin && usage.llm_cost_usd != null && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      (${usage.llm_cost_usd.toFixed(2)})
                    </span>
                  )}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {usage.total_input_tokens.toLocaleString()} input /{" "}
                  {usage.total_output_tokens.toLocaleString()} output
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mic className="h-4 w-4" />
                  <p className="text-sm font-medium">Transcription</p>
                </div>
                {(() => {
                  const t = formatTranscriptionTime(usage.total_audio_minutes);
                  return (
                    <p className="mt-2 text-2xl font-bold text-foreground">
                      {t.value} {t.unit}
                      {user?.is_admin &&
                        usage.transcription_cost_usd != null && (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            (${usage.transcription_cost_usd.toFixed(2)})
                          </span>
                        )}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Progress bars for trialing users with configured limits */}
            {subscriptionStatus?.is_trialing && (
              <div className="mt-4 space-y-3">
                {subscriptionStatus.trial_llm_cost_limit_usd !== null &&
                  subscriptionStatus.trial_llm_cost_used_usd !== null && (
                    <CostProgressBar
                      used={subscriptionStatus.trial_llm_cost_used_usd}
                      limit={subscriptionStatus.trial_llm_cost_limit_usd}
                      label="LLM trial limit"
                      isAdmin={!!user?.is_admin}
                    />
                  )}
                {subscriptionStatus.trial_transcription_cost_limit_usd !==
                  null &&
                  subscriptionStatus.trial_transcription_cost_used_usd !==
                    null && (
                    <CostProgressBar
                      used={
                        subscriptionStatus.trial_transcription_cost_used_usd
                      }
                      limit={
                        subscriptionStatus.trial_transcription_cost_limit_usd
                      }
                      label="Transcription trial limit"
                      isAdmin={!!user?.is_admin}
                    />
                  )}
                {subscriptionStatus.trial_total_cost_limit_usd !== null &&
                  subscriptionStatus.trial_total_cost_used_usd !== null && (
                    <CostProgressBar
                      used={subscriptionStatus.trial_total_cost_used_usd}
                      limit={subscriptionStatus.trial_total_cost_limit_usd}
                      label="Total trial limit"
                      isAdmin={!!user?.is_admin}
                    />
                  )}
              </div>
            )}
          </Card>
        )}
    </div>
  );
}
